import { useEffect, useState } from 'react';
import SidebarButton from "./SidebarButton";
import { Logout, WhiteAccount, SmallLetter, Delete, BigLetter, Account } from '../../../ui/icons';
import Modal from './Modal';
import { useDispatch, useSelector } from "react-redux";
import { registerLogout } from "../../../store/formSlice";
import { useNavigate } from "react-router-dom";
import { logout, updateStudentProfile } from "../../../store/authSlice";

const ProfileSidebar = ({ activeTab, setActiveTab }) => {
    const [showModal, setShowModal] = useState(false);
    const [modalMessage, setModalMessage] = useState('');
    const [isEditing, setIsEditing] = useState(false);
    const [editedFirstName, setEditedFirstName] = useState('');
    const [editedLastName, setEditedLastName] = useState('');
    const [originalFirstName, setOriginalFirstName] = useState('');
    const [originalLastName, setOriginalLastName] = useState('');
    
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { token } = useSelector(state => state.auth);
    const { token: registerToken } = useSelector(state => state.form);
    const { user, loading, error } = useSelector((store) => store.auth);

    const openModal = (message) => {
        setModalMessage(message);
        setShowModal(true);
    };

    useEffect(() => {
        if (!token && !registerToken) {
            navigate("/signin");
        }
    }, [token, registerToken, navigate]);

    useEffect(() => {
        if (user) {
            setEditedFirstName(user.first_name || '');
            setEditedLastName(user.last_name || '');
            setOriginalFirstName(user.first_name || '');
            setOriginalLastName(user.last_name || '');
        }
    }, [user]);

    const closeModal = () => {
        setShowModal(false);
        setModalMessage('');
    };

    const handleConfirm = () => {
        closeModal();
    };

    const logOut = () => {
        dispatch(logout());
        dispatch(registerLogout());
        navigate("/signin");
    };

    const startEditing = () => {
        setIsEditing(true);
    };

    const cancelEditing = () => {
        setEditedFirstName(originalFirstName);
        setEditedLastName(originalLastName);
        setIsEditing(false);
    };

    const saveChanges = async () => {
        if (!editedFirstName.trim() || !editedLastName.trim()) {
            openModal("First name and last name cannot be empty");
            return;
        }

        // Проверяем, изменились ли данные
        if (editedFirstName === originalFirstName && editedLastName === originalLastName) {
            setIsEditing(false);
            return;
        }

        try {
            const resultAction = await dispatch(updateStudentProfile({
                first_name: editedFirstName.trim(),
                last_name: editedLastName.trim()
            }));

            if (updateStudentProfile.fulfilled.match(resultAction)) {
                setOriginalFirstName(editedFirstName);
                setOriginalLastName(editedLastName);
                setIsEditing(false);
                openModal("Changes saved successfully");
            } else {
                throw new Error(resultAction.payload || "Update failed");
            }
        } catch (error) {
            console.error("Error updating profile:", error);
            let errorMessage = "Error updating profile data";
            
            if (error.message) {
                errorMessage = error.message;
            } else if (typeof error === 'string') {
                errorMessage = error;
            }
            
            openModal(errorMessage);
            // Восстанавливаем оригинальные значения в случае ошибки
            setEditedFirstName(originalFirstName);
            setEditedLastName(originalLastName);
        }
    };

    const handleFirstNameChange = (e) => {
        setEditedFirstName(e.target.value);
    };

    const handleLastNameChange = (e) => {
        setEditedLastName(e.target.value);
    };

    const handleDeleteAccount = () => {
        openModal("Are you sure you want to delete your account? This action cannot be undone.");
    };

    return (
        <div className='flex flex-col bg-[#FFFFFF] px-5 py-6 md:w-1/4 text-center items-center rounded-xl'>
            <p className='pb-5 text-xl'>Personal Details</p>
            <img 
                src={`${user?.photo ? user?.photo : "./images/Profile/beg.png"}`} 
                className='w-1/3 rounded-[50%] mb-4'
                style={{aspectRatio: "1 / 1 "}}
                alt="Profile image" 
            />
            
            {isEditing ? (
                <div className="w-full mt-3 space-y-3">
                    <div className="flex flex-col">
                        <label htmlFor="firstName" className="text-sm text-left mb-1 text-gray-600">
                            First Name
                        </label>
                        <input
                            id="firstName"
                            type="text"
                            value={editedFirstName}
                            onChange={handleFirstNameChange}
                            className="border border-gray-300 rounded-md p-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                            placeholder="Enter first name"
                            disabled={loading}
                        />
                    </div>
                    <div className="flex flex-col">
                        <label htmlFor="lastName" className="text-sm text-left mb-1 text-gray-600">
                            Last Name
                        </label>
                        <input
                            id="lastName"
                            type="text"
                            value={editedLastName}
                            onChange={handleLastNameChange}
                            className="border border-gray-300 rounded-md p-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                            placeholder="Enter last name"
                            disabled={loading}
                        />
                    </div>
                    <div className="flex justify-center space-x-3 mt-4">
                        <button
                            onClick={saveChanges}
                            disabled={loading}
                            className="bg-green-500 text-white px-4 py-2 rounded-md text-sm hover:bg-green-600 disabled:bg-green-300 disabled:cursor-not-allowed min-w-[80px] transition-colors"
                        >
                            {loading ? 'Saving...' : 'Save'}
                        </button>
                        <button
                            onClick={cancelEditing}
                            disabled={loading}
                            className="bg-gray-500 text-white px-4 py-2 rounded-md text-sm hover:bg-gray-600 disabled:bg-gray-300 disabled:cursor-not-allowed min-w-[80px] transition-colors"
                        >
                            Cancel
                        </button>
                    </div>
                </div>
            ) : (
                <>
                    <div className="flex items-center justify-center mt-3 mb-2">
                        <h2 className="font-semibold text-xl mr-3">
                            {user?.first_name} {user?.last_name}
                        </h2>
                        <button
                            onClick={startEditing}
                            className="text-blue-500 hover:text-blue-700 text-sm font-medium underline ml-2 transition-colors"
                            title="Edit name"
                            disabled={loading}
                        >
                            Edit
                        </button>
                    </div>
                    <p className="flex text-[13px] gap-1 items-center text-gray-600">
                        <SmallLetter /> {user?.email}
                    </p>
                </>
            )}
            
            <div className="flex flex-col w-full gap-2 pt-6 mt-4 border-t border-gray-200">
                <SidebarButton 
                    active={activeTab === 'personalData'} 
                    onClick={() => setActiveTab('personalData')} 
                    icon={activeTab === 'personalData' ? <WhiteAccount /> : <Account />} 
                    text="Personal Data" 
                />
                <SidebarButton 
                    active={activeTab === 'accountSecurity'} 
                    onClick={() => setActiveTab('accountSecurity')} 
                    icon={<BigLetter />} 
                    text="Account Security" 
                />
                <SidebarButton 
                    onClick={logOut}
                    icon={<Logout />} 
                    text="Log Out" 
                />
                <SidebarButton 
                    onClick={handleDeleteAccount}
                    icon={<Delete />} 
                    text="Delete Account" 
                    textColor="#D40000" 
                    borderColor="#D40000" 
                />
            </div>

            {showModal && (
                <Modal
                    message={modalMessage}
                    onClose={closeModal}
                    onConfirm={handleConfirm}
                    show={showModal}
                />
            )}

            {/* Отображение ошибок из Redux */}
            {error && (
                <div className="mt-4 p-2 bg-red-100 border border-red-400 text-red-700 rounded">
                    {typeof error === 'object' ? JSON.stringify(error) : error}
                </div>
            )}
        </div>
    );
};

export default ProfileSidebar;